import { useAccount, useMsal } from '@azure/msal-react'
import { EFinancingProgram } from '@if/core/types'
import {
  AccountCircle as AccountCircleIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Calculate as CalculatorIcon,
  Logout as LogoutIcon,
  Message as MessageIcon,
  MonetizationOn as MonetizationOnIcon,
  Translate as TranslateIcon,
  Tune as TuneIcon,
} from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles'
import { AddApplicationButton, AddApplicationListItem } from '@src/components/AddApplication'
import { ResourcesHeaderList, ResourcesMobileList } from '@src/components/Resources'
import { useUserSupportsProgram } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { useUser, useUserPermissions } from '@src/contexts/AppContext'
import { useColorMode } from '@src/contexts/ThemeContext'
import { useUnreadEntries } from '@src/data/api/chat-message/chat-message-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { CLEAR_CACHE_EXCEPTION_KEY, EThemeMode, HOME_PAGE } from '@src/types/Constants'
import { merge } from 'lodash-es'
import { MouseEventHandler, useMemo, useState, type JSX } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import EditProfileDialog from './EditProfileDialog'
import useFeatureFlags from '@src/hooks/config-hooks'
import { useChatMessageSignalR } from '@src/hooks/useChatMessageSignalR'

const clearCache = () => {
  const persistentValues: Record<string, string> = {}
  CLEAR_CACHE_EXCEPTION_KEY.forEach((key) => {
    const cachedValue = localStorage.getItem(key)
    if (cachedValue) persistentValues[key] = cachedValue
  }, {})
  localStorage.clear()
  Object.entries(persistentValues).forEach(([key, value]) => {
    localStorage.setItem(key, value)
  })
}

const stringAvatar = (name: string) => {
  return `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`
}

const innerTheme = (theme: Theme) =>
  createTheme(
    merge(theme, {
      components: {
        MuiButton: {
          defaultProps: {
            variant: 'text',
            color: 'inherit',
          },
        },
        MuiIconButton: {
          defaultProps: {
            color: 'inherit',
          },
        },
      },
    }),
  )

const AppHeader = (): JSX.Element => {
  const { palette, logoContrast, logoAlt, phone } = useTheme()
  const { toggleColorMode } = useColorMode()
  const { t, i18n } = useTranslation()
  const [openDrawer, setOpenDrawer] = useState(false)
  const { instance } = useMsal()
  const account = useAccount()
  const location = useLocation()
  const user = useUser()
  const { canCreateApplications } = useUserPermissions()
  const [merchant] = useMerchantById(user?.merchantId ?? '')
  const [anchorUserEl, setAnchorUserEl] = useState<null | HTMLElement>(null)
  const [openEditProfile, setOpenEditProfile] = useState(false)
  const navigate = useNavigate()
  const supportsProducts = useUserSupportsProgram(EFinancingProgram.Products)
  const supportsPersonal = useUserSupportsProgram(EFinancingProgram.Personal)
  const { flags } = useFeatureFlags()
  const hrefPhone = useMemo(() => `tel:${phone.replaceAll(/[- ()]/g, '')}`, [phone])

  const [unreadEntries] = useUnreadEntries()
  const totalUnread = useMemo(() => unreadEntries.length, [unreadEntries])

  // Real-time unread updates via SignalR
  useChatMessageSignalR({
    merchantId: user?.merchantId ?? '',
    enabled: !!flags.UseInstantMessage && !!user?.merchantId,
  })

  const language = i18n.language
  const handleMenuOpenProfile = () => {
    setAnchorUserEl(null)
    setOpenEditProfile(true)
  }

  const handleDrawerOpenProfile = () => {
    setOpenDrawer(false)
    setOpenEditProfile(true)
  }

  const openUserMenu = Boolean(anchorUserEl)

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorUserEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorUserEl(null)
  }

  const handleLogout = async () => {
    clearCache()
    if (account) await instance.logoutRedirect({ account })
    else navigate(HOME_PAGE)
  }

  const changeLanguage = () => {
    i18n.changeLanguage(i18n.resolvedLanguage === 'fr' ? 'en' : 'fr').catch(reportErrorToConsole)
  }

  const handleChatMessageClick = () => {
    navigate('/chat-dashboard')
  }

  return (
    <>
      <AppBar
        position="static"
        color="secondary"
        enableColorOnDark
        sx={{
          displayPrint: 'none',
          backgroundImage: 'none',
        }}
      >
        <ThemeProvider theme={innerTheme}>
          <Toolbar>
            <IconButton sx={{ display: { lg: 'none' } }} size="large" onClick={() => setOpenDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Button className="iFinanceBanner" href={HOME_PAGE} sx={{ flexShrink: 0, mr: 2 }}>
              <img src={logoContrast} alt={logoAlt} style={{ height: '30px' }} />
            </Button>

            <Stack direction="row" spacing={1} sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' } }}>
              {/* Navigation */}
              <Button startIcon={<MonetizationOnIcon />} href="/credits">
                {t('menus.application')}
              </Button>
              {canCreateApplications && (
                <AddApplicationButton supportedFinancingPrograms={merchant?.supportedFinancingPrograms ?? []} />
              )}
              {(supportsProducts || supportsPersonal) && (
                <Button startIcon={<CalculatorIcon />} href="/loan-calculator">
                  {t('menus.calculator')}
                </Button>
              )}
              {flags.UseCustomFlashPlan && user?.rights.canManagePromotions && (
                <Button startIcon={<TuneIcon />} href="/custom-buydown">
                  {t('menus.customBuydown')}
                </Button>
              )}
              {flags.UseInstantMessage && (
                <Button
                  startIcon={
                    <Badge badgeContent={totalUnread} color="error" max={99}>
                      <MessageIcon />
                    </Badge>
                  }
                  onClick={handleChatMessageClick}
                >
                  {t('menus.instantMessage')}
                </Button>
              )}
              <ResourcesHeaderList
                supportedFinancingPrograms={merchant?.supportedFinancingPrograms ?? []}
                language={language}
              />
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ display: { xs: 'none', lg: 'flex' } }}
            >
              {/* Actions */}
              <Typography
                variant="caption"
                sx={{ display: { xs: 'none', lg: 'block' }, maxWidth: '300px', textAlign: 'end', mr: 1 }}
              >
                <Trans
                  i18nKey="common.contactUs"
                  values={{ phone }}
                  components={{ a: <Link href={hrefPhone} style={{ textWrap: 'nowrap' }} /> }}
                />
              </Typography>

              <Tooltip title={t('common.targetLanguageLong')}>
                <Button onClick={changeLanguage}>{t('common.targetLanguage')}</Button>
              </Tooltip>
              <Tooltip title={palette.mode === EThemeMode.Dark ? t('common.lightMode') : t('common.darkMode')}>
                <IconButton onClick={() => toggleColorMode()}>
                  {palette.mode === EThemeMode.Dark ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('menus.account')}>
                <IconButton
                  aria-controls={openUserMenu ? 'user-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openUserMenu ? 'true' : undefined}
                  onClick={handleClick}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
              <Menu id="user-menu" anchorEl={anchorUserEl} open={openUserMenu} onClose={handleClose}>
                <MenuItem onClick={handleMenuOpenProfile}>{t('menus.profile')}</MenuItem>
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  {t('menus.logout')}
                </MenuItem>
              </Menu>
            </Stack>
          </Toolbar>
        </ThemeProvider>
      </AppBar>
      <Drawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        slotProps={{
          paper: { sx: { width: '280px', maxWidth: '90%' } },
        }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack spacing={1} sx={{ p: 2 }}>
            <Avatar>{stringAvatar(user!.fullName)}</Avatar>
            <Typography variant="subtitle1">{user!.fullName}</Typography>
          </Stack>
          <Divider />
          <List>
            {/* Navigation */}
            <ListItemButton onClick={handleDrawerOpenProfile}>
              <ListItemIcon>
                <AccountCircleIcon />
              </ListItemIcon>
              <ListItemText primary={t('menus.profile')} />
            </ListItemButton>
            <ListItemButton selected={location.pathname === '/credits'} href="/credits">
              <ListItemIcon>
                <MonetizationOnIcon />
              </ListItemIcon>
              <ListItemText primary={t('menus.application')} />
            </ListItemButton>
            {(supportsProducts || supportsPersonal) && (
              <ListItemButton selected={location.pathname === '/loan-calculator'} href="/loan-calculator">
                <ListItemIcon>
                  <CalculatorIcon />
                </ListItemIcon>
                <ListItemText primary={t('menus.calculator')} />
              </ListItemButton>
            )}
            {flags.UseCustomFlashPlan && user?.rights.canManagePromotions && (
              <ListItemButton selected={location.pathname === '/custom-buydown'} href="/custom-buydown">
                <ListItemIcon>
                  <TuneIcon />
                </ListItemIcon>
                <ListItemText primary={t('menus.customBuydown')} />
              </ListItemButton>
            )}
            {flags.UseInstantMessage && (
              <ListItemButton onClick={handleChatMessageClick}>
                <ListItemIcon>
                  <Badge badgeContent={totalUnread} color="error" max={99}>
                    <MessageIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary={t('menus.instantMessage')} />
              </ListItemButton>
            )}
            <AddApplicationListItem supportedFinancingPrograms={merchant?.supportedFinancingPrograms ?? []} />
            <ResourcesMobileList
              supportedFinancingPrograms={merchant?.supportedFinancingPrograms ?? []}
              language={language}
            />
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ p: 1 }}>
            <Trans
              i18nKey="common.contactUs"
              values={{ phone }}
              components={{ a: <Link href={hrefPhone} style={{ textWrap: 'nowrap' }} /> }}
            />
          </Typography>
          <Divider />
          <List>
            {/* Actions */}
            <ListItemButton onClick={() => toggleColorMode()}>
              <ListItemIcon>
                {palette.mode === EThemeMode.Dark ? <Brightness7Icon /> : <Brightness4Icon />}
              </ListItemIcon>
              <ListItemText primary={palette.mode === EThemeMode.Dark ? t('common.lightMode') : t('common.darkMode')} />
            </ListItemButton>
            <ListItemButton onClick={changeLanguage}>
              <ListItemIcon>
                <TranslateIcon />
              </ListItemIcon>
              <ListItemText primary={t('common.targetLanguageLong')} />
            </ListItemButton>
            <Divider />
            <ListItemButton onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText primary={t('menus.logout')} />
            </ListItemButton>
          </List>
        </Stack>
      </Drawer>
      <EditProfileDialog open={openEditProfile} handleClose={() => setOpenEditProfile(false)} />
    </>
  )
}

export default AppHeader
